import { Pipe, PipeTransform } from '@angular/core';
import { ItemConfig, ItemProfile, ItemProperties, ItemResource, Link, ResourceMeta } from '@ngx-hal/models';
import { PropertiesProfilesPipe } from '../properties-profiles/properties-profiles.pipe';
import { PropertyResourceMetaPipe } from '../property-resource-meta/property-resource-meta.pipe';

@Pipe({
  name: 'filterParams',
  standalone: false
})
export class FilterParamsPipe implements PipeTransform {
  /**
   * Transforms a resource metadata object and associated profiles, configuration, and sample data
   * into a record mapping item property names to their string values, suitable for filtering.
   *
   * @template Entity - The type of the resource entity extending ItemResource.
   * @param resourceMeta - The metadata describing the resource entity.
   * @param itemsProfileMap - Optional map of profile hrefs to item profiles for the entity.
   * @param itemConfig - Optional configuration object for item properties, including filter values and custom filters.
   * @param sample - Optional partial sample of the entity, providing values for filtering.
   * @param prefix - Optional string prefix to prepend to property names in the resulting record.
   * @param showAllLabels - Optional flag to include hidden properties in the output.
   * @returns A record mapping item property names (with optional prefix) to their string values,
   *          based on the sample data and configuration, suitable for use as filter parameters.
   */
  transform<Entity extends ItemResource>(
    resourceMeta: ResourceMeta<Entity>,
    itemsProfileMap?: Map<Link['href'], ItemProfile<Entity>>,
    itemConfig?: ItemConfig<Entity>,
    sample?: Partial<Entity>,
    prefix?: string,
    showAllLabels?: boolean
  ): Record<ItemProperties<Entity>, string> {
    const itemProfile = itemsProfileMap?.get(resourceMeta.profile.href);

    return itemProfile
      ? Object.fromEntries(
          (new PropertiesProfilesPipe()
            .transform(itemProfile, itemConfig, { remove:{
              children: true} ,keep: {
                hidden: showAllLabels ? true : false
              } }) ?? [])
            .filter(
              property =>
                sample?.[property.alps.name] !== undefined ||
                itemConfig?.[property.alps.name]?._filterValue !== undefined,
            )
            .map(property => {
              const subResourceMeta = new PropertyResourceMetaPipe().transform(property);
              if (subResourceMeta) {
                return Object.entries(
                  this.transform(
                    subResourceMeta,
                    itemsProfileMap,
                    itemConfig?.[property.alps.name],
                    sample?.[property.alps.name] as Partial<Entity>,
                    `${property.alps.name}.`,
                  ),
                );
              } else {
                if (itemConfig?.[property.alps.name]?._customFilter) {
                  return [];
                }
                // if sample value is absent and filterValue (defaut valie in config) then it picks that value
                // to pass comma separated integers correctly
                if (itemConfig?.[property.alps.name]?._customFilter || (property.schema?.type === 'integer' && sample?.[property.alps.name]?.toString().includes(','))) {
                  return [];
                }
                if (itemConfig?.[property.alps.name]?._filterValue && !sample?.[property.alps.name]) {
                  return [
                  [`${prefix ?? ''}${property.alps.name}`, `${itemConfig?.[property.alps.name]?._filterValue}`],
                  ];
                }
                return [[`${prefix ?? ''}${property.alps.name}`, `${sample?.[property.alps.name]}`]];
              }
            })
            .flat(),
        )
      : {} as Record<ItemProperties<Entity>, string>;
  }
}
